import React from 'react';

import { v4 as uuidv4 } from 'uuid';
import { Divider, Stack, TextField } from '@mui/material';
import { Label } from '@common/Label';
import { Select } from '@common/Select';
import { Option } from '@common/BucketInput/BucketField';
import { BucketInput } from '@common/BucketInput/BucketInput';
import { useQuery } from '@tanstack/react-query';
import { getBuckets, GetBucketsParams, TelnyxBucket } from '@requests/buckets';
import { useParams } from 'react-router-dom';
import { Expression } from '@common/Expression';
import { BucketSearchMetadata } from '../../../tool.types';
import { RequestError } from '@requests/common';
import {
  TTBackupOutput,
  TTBuckets,
  TTMaxResults,
  TTMaxTokens,
  TTSearchTerm,
  TTSearchType,
} from '@common/Node/NodeSidebar/Extensions/BucketSearch';

interface Props {
  metadata: BucketSearchMetadata;
  setToolMetadata: React.Dispatch<React.SetStateAction<BucketSearchMetadata>>;
  toolArgumentOptions: object;
}

export const BucketSearchExecution: React.FC<Props> = ({ metadata, setToolMetadata, toolArgumentOptions }) => {
  const { service_id } = useParams();

  const { data: buckets = [] } = useQuery<
    TelnyxBucket[],
    RequestError,
    TelnyxBucket[],
    [string, string, GetBucketsParams]
  >({
    queryKey: ['globalFiles', 'buckets', { service_id: service_id as string, all: true }],
    queryFn: getBuckets,
  });

  const bucketOptions = buckets.map((item: any) => ({ label: item.name, value: item.name, type: 'telnyx' }));
  const isMaxBuckets = metadata.indexes.length >= bucketOptions.length;

  const updateTextField = (paramName: 'search_descriptor' | 'bucket_search_type' | 'backup_output', value: string) => {
    setToolMetadata((prevState) => ({ ...prevState, [paramName]: value }));
  };

  const updateNumericField = (paramName: 'max_tokens' | 'max_results', value: number) => {
    setToolMetadata((prevState) => ({ ...prevState, [paramName]: value }));
  };

  const addIndex = () => {
    if (isMaxBuckets) return;
    const value = { id: uuidv4(), index: '', weight: 1, type: 'telnyx' };
    setToolMetadata((prevState) => ({ ...prevState, indexes: prevState.indexes.concat(value) }));
  };

  const removeIndex = (indexId: string) => {
    setToolMetadata((prevState) => ({
      ...prevState,
      indexes: prevState.indexes.filter((item) => item.id !== indexId),
    }));
  };

  const updateIndexField = (indexField: string, indexFieldId: string, indexValue: string) => {
    setToolMetadata((prevState) => ({
      ...prevState,
      indexes: prevState.indexes.map((item) => {
        if (item.id === indexFieldId) return { ...item, [indexField]: indexValue };
        return item;
      }),
    }));
  };

  const searchTypeOptions: Option[] = [
    { label: 'List & Search', value: 'list_and_search' },
    { label: 'Direct Search', value: 'direct_search' },
    { label: 'List', value: 'list' },
  ];

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Label title='Search Term' tooltip={TTSearchTerm} />
        <Expression
          key={`search_descriptor`}
          keyProp={`search_descriptor`}
          value={metadata.search_descriptor}
          title={'Search Descriptor'}
          description={TTSearchTerm}
          setValue={(newValue: string) => updateTextField('search_descriptor', newValue)}
          options={toolArgumentOptions}
          placeholder='Search Term'
        />
      </Stack>
      <Stack spacing={1}>
        <Label title='Search Method' tooltip={TTSearchType} />
        <Select
          value={metadata.bucket_search_type}
          setValue={(value: string) => updateTextField('bucket_search_type', value)}
          options={searchTypeOptions}
          placeholder='Select an index search type'
        />
      </Stack>
      <Stack display='grid' gridTemplateColumns='1fr 1fr' gap='0.5rem'>
        <Stack spacing='8px'>
          <Label title='Max Tokens' tooltip={TTMaxTokens} />
          <TextField
            size='small'
            fullWidth
            placeholder='Max Tokens'
            value={metadata.max_tokens}
            type='number'
            onChange={(e) => {
              if (Number.parseInt(e.target.value) < 0) return;
              updateNumericField('max_tokens', Number.parseInt(e.target.value));
            }}
          />
        </Stack>
        <Stack spacing='8px'>
          <Label title='Max Results' tooltip={TTMaxResults} />
          <TextField
            size='small'
            fullWidth
            placeholder='Max Results'
            value={metadata.max_results}
            type='number'
            onChange={(e) => {
              if (Number.parseInt(e.target.value) < 1) return;
              updateNumericField('max_results', Number.parseInt(e.target.value));
            }}
          />
        </Stack>
      </Stack>
      <Stack spacing='8px'>
        <Label
          title='Buckets'
          tooltip={TTBuckets}
          onClick={metadata.indexes.length !== 0 && !isMaxBuckets ? addIndex : undefined}
        />
        <BucketInput
          hideAddIndex
          indexes={metadata.indexes}
          addIndex={addIndex}
          updateField={updateIndexField}
          removeIndex={removeIndex}
          allBuckets={true}
        />
      </Stack>
      <Divider />
      <Stack spacing={1}>
        <Label title='Backup Tool Output' tooltip={TTBackupOutput} />
        <TextField
          fullWidth
          size='small'
          value={metadata.backup_output}
          onChange={(e) => updateTextField('backup_output', e.target.value)}
          placeholder='There was no documentation found for the users question.'
        />
      </Stack>
    </Stack>
  );
};
